import { Component, Input } from '@angular/core';
import { ProductStore } from '../../data/product/product-features.data';
import { storeAppListPrice } from '../../data/product/product-lookup.data';
import { AnalyticsService } from '../../service/analytics/analytics.service';
import { CartPopupService } from '../../service/cart/cart-popup.service';
import { StoreService } from '../../service/store/store.service';
import { openInMacAppStore } from '../../util/mac-app-store';

/** One of our apps, as a tool page shows it under "More … Tools". */
export interface MoreToolApp {
  eyebrow: string;
  icon: string;
  name: string;
  lead: string;
  text: string;
  facts: string[];
  link: string;
  cta: string;
  /** sold on the site: Add to cart and its price while the store sells it */
  cartSku?: string;
  /** its own store (Mac App Store, Chrome Web Store, ...) */
  store?: ProductStore;
  /** the note in place of a store not out yet ("Coming Soon to the Mac App Store") */
  soon?: string;
  shot: { src: string; alt: string; width: number; height: number };
  /** an app on several platforms (the Color Picker): one per platform, picked in the dock under the picture; each
   *  replaces the fields it brings */
  variants?: MoreToolVariant[];
}

export type MoreToolVariant = Partial<Omit<MoreToolApp, 'variants'>> & { key: string; chip: string; chipLogo?: string };

/**
 * A tool page's "More … Tools" section (owner 2026-10-22: More AI Tools on the AI Cost Calculator, More Color Tools
 * on the color tool): the tool-page row of two cards, the app in a box of its own in the right one, with the same
 * buttons as its Apps & Plugins box (Discover More, Add to cart with the price, its store or its Coming Soon note).
 */
@Component( {
  selector: 'app-more-tools',
  templateUrl: './more-tools.component.html',
  styleUrls: [ './more-tools.component.scss' ],
} )
export class MoreToolsComponent {
  @Input() sectionKey = 'more';
  @Input() title = '';
  @Input() lead = '';
  @Input() app!: MoreToolApp;
  /** where the visit came from, for the cart and install events */
  @Input() source = 'tool-page';
  /** the platform picked in the dock */
  active = 0;

  /** the app as the picked platform shows it */
  get current(): MoreToolApp {
    const v = this.app?.variants?.[ this.active ];
    return v ? { ...this.app, ...v } as MoreToolApp : this.app;
  }

  pick( index: number ): void {
    this.active = index;
  }

  constructor(
    private storeService: StoreService,
    private cartPopupService: CartPopupService,
    private analyticsService: AnalyticsService,
  ) {
  }

  get onSale(): boolean {
    const sku = this.current?.cartSku;
    return !!sku && this.storeService.availability( sku ) === 'buy';
  }

  get price(): string | null {
    const sku = this.current?.cartSku;
    return sku ? this.storeService.priceLabel( sku, storeAppListPrice( sku ) ) : null;
  }

  addToCart(): void {
    const sku = this.current.cartSku;
    if ( sku ) this.cartPopupService.add( sku, this.source );
  }

  onInstall( store: ProductStore, event?: Event ): void {
    this.analyticsService.logEvent( store.event, { ...store.attributes, from: this.source } );
    if ( store.macApp ) openInMacAppStore( event, store.url );
  }
}
